import { useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  UserCheck,
} from "lucide-react";

import { Flag, Logo } from "@/components/bits";
import { PasswordInput } from "@/components/password-input";
import { countryInfo, registrationPhone } from "@/lib/countries";
import { CountrySelect } from "@/components/country-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { accountApi, ApiError } from "@/services/api-client";
import { useI18n, LanguageSwitcher } from "@/i18n/i18n-context";

export function RegisterPage() {
  const { t } = useI18n();
  const [, nav] = useLocation();
  const [accountType, setAccountType] = useState<"BUSINESS" | "INDIVIDUAL">(
    "BUSINESS",
  );
  const [name, setName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [country, setCountry] = useState("CM");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [matricule, setMatricule] = useState("");
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [resendState, setResendState] = useState<
    "" | "busy" | "done" | "error"
  >("");
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const phoneNumber = registrationPhone(country, phone);
    if (!phoneNumber) {
      setError(
        "Saisissez un numéro valide pour le pays sélectionné. L’indicatif est ajouté automatiquement.",
      );
      return;
    }
    if (!name.trim() || !accountName.trim() || !email.trim() || !password) {
      setError("Remplissez tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);

    try {
      const response = await accountApi<Record<string, unknown>>(
        "/auth/register",
        {
          method: "POST",
          body: {
            account_name: accountName.trim(),
            account_type: accountType,
            country,
            email: email.trim(),
            name: name.trim(),
            password,
            phone_number: phoneNumber,
          },
        },
      );
      const user = (response.data.user ?? {}) as Record<string, unknown>;
      setMatricule(user.matricule ? String(user.matricule) : "");
      setVerificationRequired(response.data.verification_required === true);
      setPassword("");
      setSuccess(true);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "La création du compte est temporairement indisponible.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function resend() {
    setResendState("busy");
    try {
      await accountApi("/auth/email/verification/resend", {
        method: "POST",
        body: { email: email.trim() },
      });
      setResendState("done");
    } catch {
      setResendState("error");
    }
  }

  return (
    <main className="relative grid min-h-[100dvh] bg-[#f4f7fb] lg:grid-cols-[1.05fr_0.95fr]">
      <div className="absolute right-4 top-4 z-20">
        <LanguageSwitcher variant="auth" />
      </div>

      <section className="relative hidden overflow-hidden bg-[#061a45] p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-40 -top-40 h-96 w-96 rounded-full bg-cyan-400/15 blur-3xl" />
        <div className="absolute -bottom-32 left-20 h-80 w-80 rounded-full bg-emerald-400/15 blur-3xl" />
        <Logo light />
        <div className="relative max-w-xl">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200">
            <ShieldCheck className="h-4 w-4" /> {t("auth.registerBadge")}
          </p>
          <h1 className="text-5xl font-black leading-[1.08] tracking-tight">
            {t("auth.registerHeroTitle")}
          </h1>
          <p className="mt-6 max-w-lg text-base leading-7 text-slate-300">
            {t("auth.registerHeroSub")}
          </p>
        </div>
        <p className="relative text-xs text-slate-400">
          {t("auth.registerHeroCompliance")}
        </p>
      </section>

      <section className="flex items-center justify-center px-5 py-10 sm:px-10">
        <div className="w-full max-w-md">
          <div className="mb-6 lg:hidden">
            <Logo />
          </div>

          {success ? (
            <div className="rounded-2xl border bg-card p-6 shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h2
                className="mt-4 text-2xl font-black tracking-tight text-[#0b1f4b]"
                data-testid="text-register-success"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -left-[10000px] h-px w-px overflow-hidden"
                >
                  <label htmlFor="registration-website">Website</label>
                  <input
                    id="registration-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value=""
                    onChange={() => undefined}
                  />
                </div>
                {t("auth.registrationSuccessTitle")}
              </h2>
              {matricule && (
                <>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t("auth.matriculeLabel")}
                  </p>
                  <div
                    className="my-4 rounded-lg bg-muted p-3 text-center font-mono text-base font-bold text-primary"
                    data-testid="text-register-matricule"
                  >
                    {matricule}
                  </div>
                </>
              )}
              {verificationRequired && (
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {t("auth.verifyEmailNotice", { email })}
                </p>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                {t("auth.kybPendingNotice", {
                  type: accountType === "BUSINESS" ? "KYB" : "KYC",
                })}
              </p>
              <div className="mt-6 flex flex-col gap-2">
                <Button
                  onClick={() => nav("/login")}
                  className="w-full gap-2"
                  data-testid="button-go-login"
                >
                  {t("auth.signInBtn")} <ArrowRight className="h-4 w-4" />
                </Button>
                {verificationRequired && (
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={resendState === "busy"}
                    onClick={() => void resend()}
                    data-testid="button-register-resend"
                  >
                    {resendState === "busy" && (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    )}
                    {t("auth.resendVerificationLink")}
                  </Button>
                )}
                {resendState === "done" && (
                  <p className="text-xs text-emerald-700">
                    {t("auth.resendSuccess")}
                  </p>
                )}
                {resendState === "error" && (
                  <p className="text-xs text-rose-700">
                    {t("auth.resendFailed")}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                {t("auth.registerCategory")}
              </p>
              <h2 className="mt-2 text-3xl font-black tracking-tight text-[#0b1f4b]">
                {t("auth.registerTitle")}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("auth.registerSub")}
              </p>

              <form
                onSubmit={submit}
                className="mt-6 space-y-4"
                noValidate
                data-testid="form-register"
              >
                {error && (
                  <div
                    role="alert"
                    className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
                  >
                    {error}
                  </div>
                )}
                {/* Account Type */}
                <div className="space-y-1.5">
                  <Label>{t("auth.accountTypeLabel")}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setAccountType("BUSINESS")}
                      className={`rounded-lg border p-2.5 text-left transition ${
                        accountType === "BUSINESS"
                          ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                          : "border-input hover:bg-muted/40"
                      }`}
                    >
                      <p className="text-xs font-bold">
                        {t("auth.businessKyb")}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {t("auth.businessKybDesc")}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccountType("INDIVIDUAL")}
                      className={`rounded-lg border p-2.5 text-left transition ${
                        accountType === "INDIVIDUAL"
                          ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                          : "border-input hover:bg-muted/40"
                      }`}
                    >
                      <p className="text-xs font-bold">
                        {t("auth.individualKyc")}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {t("auth.individualKycDesc")}
                      </p>
                    </button>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="name">{t("settings.fullName")}</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Alex Martin"
                      autoComplete="name"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="accountName">
                      {accountType === "BUSINESS"
                        ? t("auth.businessNameLabel")
                        : t("auth.individualNameLabel")}
                    </Label>
                    <Input
                      id="accountName"
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      placeholder={
                        accountType === "BUSINESS"
                          ? "Horizon Services SARL"
                          : "Alex Pro"
                      }
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="country">
                      {t("auth.operatingCountry")}
                    </Label>
                    <CountrySelect
                      id="country"
                      value={country}
                      onValueChange={(value) => {
                        setCountry(value);
                        setPhone("");
                      }}
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {t("auth.countryHelp")}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">{t("settings.phone")}</Label>
                    <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
                      <span
                        className="flex shrink-0 items-center gap-1.5 border-r px-2 text-sm"
                        data-testid="phone-prefix"
                      >
                        <Flag code={country} />{" "}
                        {countryInfo(country)?.dial || "—"}
                      </span>
                      <Input
                        id="phone"
                        type="tel"
                        autoComplete="tel-national"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Numéro national"
                        className="min-w-0 border-0 shadow-none focus-visible:ring-0"
                        aria-describedby="phone-help"
                        disabled={!countryInfo(country)?.dial}
                        required
                      />
                    </div>
                    <p
                      id="phone-help"
                      className="text-[11px] text-muted-foreground"
                    >
                      {t("auth.phoneHelp")}
                    </p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">{t("auth.emailLabel")}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contact@entreprise.com"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password">{t("auth.passwordLabel")}</Label>
                  <PasswordInput
                    id="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="h-11 w-full gap-2"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserCheck className="h-4 w-4" />
                  )}
                  {submitting
                    ? t("auth.creatingAccount")
                    : t("auth.createAccountBtn")}
                  {!submitting && <ArrowRight className="ml-auto h-4 w-4" />}
                </Button>
              </form>

              <div className="mt-6 flex flex-col items-center gap-2 text-center text-sm text-muted-foreground">
                <p>
                  {t("auth.alreadyRegistered")}{" "}
                  <Link
                    href="/login"
                    className="font-semibold text-primary hover:underline"
                  >
                    {t("auth.signInLink")}
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
